import { useTranslation } from 'react-i18next';

import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUIButton, SCUIDateRanger, SCUINoRecordComponent } from '../../../atomic/atoms';
import { Box, Chip, Select, MenuItem } from '@mui/material';

import TimeHelper from '../../../utils/time';

import { PaymentConsolidationAccountingStatus } from '../enum/SummaryEnums';
import airdropShipment from '../../../assets/icons/svg/pageIcons/airdropShipment.svg';
import './style.scss';

export const SummaryList = ({
    summaryList,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    handleSearch,
    isSearched,
    setSelectedSummary,
    status,
    setStatus,
    handlePrint,
    handleAxapta,
    selectedSummary,
}: any) => {
    const { t } = useTranslation();

    const statusLabels: Record<PaymentConsolidationAccountingStatus, string> = {
        [PaymentConsolidationAccountingStatus.All]: t('summary.all'),
        [PaymentConsolidationAccountingStatus.PendingAccounting]: t('summary.pendingAccounting'),
        [PaymentConsolidationAccountingStatus.AccountingComplate]: t('summary.accountingComplate'),
        [PaymentConsolidationAccountingStatus.NeedRecalculate]: t('summary.needRecalculate'),
        [PaymentConsolidationAccountingStatus.TransferToAxaptaComplete]: t('summary.transferToAxaptaComplete'),
    };

    const paymentStatusOptions = Object.entries(statusLabels).map(([value, label]) => ({
        label,
        value: Number(value),
    }));

    const columns = [
        {
            field: 'paymentConsolidationId',
            headerName: t('summary.id'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'periodBeginDate',
            headerName: t('summary.summaryDate'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => {
                return TimeHelper.formatDate(params.value);
            },
        },
        {
            field: 'createDate',
            headerName: t('summary.createdDate'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => {
                return TimeHelper.formatDate(params.value);
            },
        },
        {
            field: 'approvalDate',
            headerName: t('summary.axaptaDate'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => {
                return params.value ? TimeHelper.formatDate(params.value) : '-';
            },
        },
        {
            field: 'accountingStatusId',
            headerName: t('summary.axaptaStatus'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => {
                return getStatusTag(params.value);
            },
        },
        {
            field: 'edit',
            headerName: t('general.edit'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <SCUIButton
                        text={t('general.edit')}
                        type="primary"
                        className="summary-edit-button"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        fullwidth
                        onClick={() => handleGoDetail(params.row)}
                    />
                );
            },
        },
        {
            field: 'print',
            align: 'center',
            headerAlign: 'center',
            headerName: t('general.print'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <SCUIButton
                        text={t('general.print')}
                        type="primary"
                        className="summary-print-button"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        fullwidth
                        disabled={
                            params.row.accountingStatusId !== PaymentConsolidationAccountingStatus.PendingAccounting
                        }
                        onClick={() => handlePrint(params.row.paymentConsolidationId)}
                    />
                );
            },
        },
        {
            field: 'transferToAxapta',
            align: 'center',
            headerAlign: 'center',
            headerName: t('summary.transferToAxapta'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <SCUIButton
                        text={t('summary.transferToAxapta')}
                        className="summary-axapta-button"
                        type="primary"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        fullwidth
                        disabled={
                            params.row.accountingStatusId === PaymentConsolidationAccountingStatus.PendingAccounting
                        }
                        onClick={() => handleAxapta(params.row.paymentConsolidationId)}
                    />
                );
            },
        },
    ];

    const getStatusTag = (status: any) => {
        switch (status) {
            case PaymentConsolidationAccountingStatus.AccountingComplate:
                return (
                    <Chip
                        sx={{
                            backgroundColor: '#22aeee !important',
                            '& .MuiChip-label': {
                                color: '#004a88',
                                fontWeight: 'bold',
                            },
                        }}
                        label={t('summary.accountingComplate')}
                    />
                );
            case PaymentConsolidationAccountingStatus.NeedRecalculate:
                return (
                    <Chip
                        label={t('summary.needRecalculate')}
                        sx={{
                            backgroundColor: '#aa6861 !important',
                            '& .MuiChip-label': {
                                color: '#3c2c2c',
                                fontWeight: 'bold',
                            },
                        }}
                    />
                );
            case PaymentConsolidationAccountingStatus.PendingAccounting:
                return (
                    <Chip
                        sx={{
                            backgroundColor: '#fff3cd !important',
                            '& .MuiChip-label': {
                                color: '#c1ad61',
                                fontWeight: 'bold',
                            },
                        }}
                        label={t('summary.pendingAccounting')}
                    />
                );
            case PaymentConsolidationAccountingStatus.TransferToAxaptaComplete:
                return (
                    <Chip
                        label={t('summary.transferToAxaptaComplete')}
                        sx={{
                            backgroundColor: '#cdead8 !important',
                            '& .MuiChip-label': {
                                color: '#50755f',
                                fontWeight: 'bold',
                            },
                        }}
                    />
                );
            default:
                return <>-</>;
        }
    };

    const _handlChangeDate = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const handleGoDetail = (detail: any) => {
        setSelectedSummary(detail);
    };

    return !selectedSummary?.paymentConsolidationId ? (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    margin: '24px 0 32px 0',
                    '& .sc-btn__text': {
                        padding: '8px 16px !important',
                    },
                }}
            >
                <p style={{ fontSize: '14px', fontWeight: '600', marginRight: '12px' }}>
                    {t('summary.summaryDateRange')}:
                </p>
                <SCUIDateRanger
                    fromDate={startDate}
                    toDate={endDate}
                    setDateRange={_handlChangeDate}
                    dateFormat="YYYY-MM-DD"
                />
                <p style={{ fontSize: '14px', fontWeight: '600', marginLeft: '24px' }}>{t('summary.axaptaStatus')}:</p>
                <Select
                    value={status}
                    onChange={(event: any) => {
                        setStatus(event.target.value);
                    }}
                    sx={{ marginLeft: '12px' }}
                >
                    {(paymentStatusOptions || []).reverse().map((item: any) => (
                        <MenuItem key={item.value} id={item.value} value={item.value}>
                            {item.label}
                        </MenuItem>
                    ))}
                </Select>
                <SCUIButton
                    style={{ marginLeft: '24px' }}
                    text={t('general.search')}
                    type="primary"
                    sizeType="fixed"
                    size={'m'}
                    disabled={!startDate || !endDate}
                    onClick={() => handleSearch()}
                />
            </Box>
            <Box>
                {!isSearched ? (
                    <SCUINoRecordComponent img={airdropShipment} />
                ) : (
                    <SCUICustomDataGrid
                        getRowClassName={'summary-list-grid'}
                        rows={summaryList || []}
                        columns={columns}
                        rowHeight={75}
                        getRowId={(row: any) => row.paymentConsolidationId}
                        hideFooter
                        allrecord
                    />
                )}
            </Box>
        </Box>
    ) : (
        <></>
    );
};
